import { SkeletonLoader } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CitationsSection } from '~/components/citations-section/CitationsSection';
import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TextSection } from '~/components/text-section/TextSection';
import { ImpactedCompanies } from '~/modules/impacted-companies/ImpactedCompanies';
import { ThematicFocus } from '~/modules/thematic-focus/ThematicFocus';
import { useCatalystInsightDetails } from '~/react-query-hooks/insights/useCatalystInsights';

import { CatalystDrawerHeader } from '../catalyst-drawer-header/CatalystDrawerHeader';
import styles from './CompanyCatalystWidget.module.scss';

export type CompanyCatalystWidgetProps = {
  insightId: string;
  expandCitations?: boolean;
};

export const CompanyCatalystWidget = ({
  insightId,
  expandCitations = false,
}: CompanyCatalystWidgetProps) => {
  const { t } = useTranslation(['insights']);
  const {
    data: catalystInsight,
    isLoading,
    error,
  } = useCatalystInsightDetails(insightId, !!insightId);

  if (error) {
    throw error;
  }

  if (isLoading) {
    return (
      <SkeletonLoader
        className={styles.loadingSkeletonWrapper}
        areas={[
          {
            styles: {
              width: '100%',
              height: '64px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '136px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '400px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '400px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!catalystInsight?.identifier || !catalystInsight) {
    return null;
  }

  return (
    <ErrorBoundaryWrapper>
      <ErrorBoundaryWrapper>
        <CatalystDrawerHeader
          identifier={catalystInsight.identifier}
          catalystInsight={catalystInsight}
          isCompanyCatalyst
        />
      </ErrorBoundaryWrapper>
      <div className={styles.sectionGroupsWrapper}>
        {catalystInsight.section_groups.map(group => (
          <div
            className={styles.groupWrapper}
            key={group.title}
            data-testid="section-group-wrapper"
          >
            <div className={styles.sectionGroupTitle}>{group.title}</div>
            {group.sections.map(section => (
              <TextSection
                key={section.title}
                title={section.title}
                textContent={section.content ?? []}
                variant="secondary"
                useBulletPoints
              />
            ))}
          </div>
        ))}
        <div>
          <ThematicFocus
            themes={catalystInsight.themes}
            otherThemes={catalystInsight.other_themes}
            title={t('insights:catalyst.thematicFocus')}
            withPill
          />
        </div>
        <ImpactedCompanies
          impactedCompanies={catalystInsight.impacted_companies}
          variant="secondary"
          title={t('insights:catalyst.mostImpactedCompanies')}
          insightCreatedAt={catalystInsight.created_at}
        />
        {!!catalystInsight.citations.length && (
          <div className={styles.citationsSectionWrapper}>
            <div className={styles.citationsTitle}>
              {t('insights:catalyst.citations')}
            </div>
            <CitationsSection
              count={catalystInsight.citations.length}
              items={catalystInsight.citations}
              title={t('insights:catalyst.webLinks')}
              isExpanded={expandCitations}
            />
          </div>
        )}
      </div>
    </ErrorBoundaryWrapper>
  );
};
